import React from 'react';

import { DigitalCardComponentLayer } from './ComponentLayer';
import { DigitalCardEffectLayer } from './EffectLayer';
import { DigitalCardImageLayer } from './ImageLayer';
import { DigitalCardShaderLayer } from './ShaderLayer';
import { DigitalCardSpriteImageLayer } from './SpriteImageLayer';
import DigitalCardTextLayer from './TextLayer';
import type { CardLayer } from './types';
import { DigitalCardVideoLayer } from './VideoLayer';

/**
 * 디지털 카드 레이어 컴포넌트의 속성 타입
 */
type DigitalCardLayerProps = CardLayer & {
  faceIdx: number;
};

/**
 * [디지털 카드 레이어 컴포넌트]
 *
 * @description
 * 디지털 카드의 각 레이어를 렌더링하는 컴포넌트입니다.
 * 이미지, 비디오, 컴포넌트, 스프라이트 이미지, 효과, 셰이더, 텍스트 등 다양한 타입의 레이어를 지원합니다.
 *
 * @param props
 * ```ts
 * {
 *   idx: number;            // 레이어의 고유 식별자
 *   name: string;           // 레이어의 이름
 *   type: CardLayerType;    // 레이어 타입 ('image', 'video', 'component', 'spriteImage', 'effect', 'shader', 'text')
 *   zIndex?: number;        // 레이어의 z-index 값
 *   translateZ?: number;    // 레이어의 z축 변환 값
 *   borderRadius?: number;  // 레이어의 모서리 둥글기
 *
 *   + faceIdx: number;        // 디지털 카드의 면 인덱스
 * }
 * ```
 *
 * @components
 * - 이미지 레이어 컴포넌트
 * ```tsx
 * <DigitalCardLayer.Image />
 * ```
 * - 비디오 레이어 컴포넌트
 * ```tsx
 * <DigitalCardLayer.Video />
 * ```
 * - 컴포넌트 레이어 컴포넌트
 * ```tsx
 * <DigitalCardLayer.Component />
 * ```
 * - 스프라이트 이미지 레이어 컴포넌트
 * ```tsx
 * <DigitalCardLayer.SpriteImage />
 * ```
 * - 효과 레이어 컴포넌트
 * ```tsx
 * <DigitalCardLayer.Effect />
 * ```
 * - 셰이더 레이어 컴포넌트
 * ```tsx
 * <DigitalCardLayer.Shader />
 * ```
 * - 텍스트 레이어 컴포넌트
 * ```tsx
 * <DigitalCardLayer.Text />
 * ```
 *
 * @features
 * - 다양한 타입의 레이어 지원 (이미지, 비디오, 컴포넌트, 스프라이트, 효과, 셰이더, 텍스트)
 * - 자동 레이어 타입 감지 및 렌더링
 * - z-index 기반 레이어 순서 관리
 * - 3D 변환 지원 (translateZ)
 * - WebGL 기반 셰이더 효과 지원 (@paper-design/shaders-react)
 *
 * @see {@link CardLayer} 카드 레이어 타입 정의
 * @see {@link DigitalCardImageLayer} 이미지 레이어 컴포넌트
 * @see {@link DigitalCardVideoLayer} 비디오 레이어 컴포넌트
 * @see {@link DigitalCardComponentLayer} 컴포넌트 레이어 컴포넌트
 * @see {@link DigitalCardSpriteImageLayer} 스프라이트 이미지 레이어 컴포넌트
 * @see {@link DigitalCardEffectLayer} 효과 레이어 컴포넌트
 * @see {@link DigitalCardShaderLayer} 셰이더 레이어 컴포넌트
 * @see {@link DigitalCardTextLayer} 텍스트 레이어 컴포넌트
 */
const DigitalCardLayer = {
  Image: DigitalCardImageLayer,
  Video: DigitalCardVideoLayer,
  Component: DigitalCardComponentLayer,
  SpriteImage: DigitalCardSpriteImageLayer,
  Effect: DigitalCardEffectLayer,
  Shader: DigitalCardShaderLayer,
  Text: DigitalCardTextLayer,

  /**
   * 레이어 타입에 따라 적절한 컴포넌트를 렌더링합니다.
   */
  render: (props: DigitalCardLayerProps) => {
    const { type } = props;

    switch (type) {
      case 'image':
        return <DigitalCardImageLayer {...props} />;
      case 'video':
        return <DigitalCardVideoLayer {...props} />;
      case 'component':
        return <DigitalCardComponentLayer {...props} />;
      case 'spriteImage':
        return <DigitalCardSpriteImageLayer {...props} />;
      case 'effect':
        return <DigitalCardEffectLayer {...props} />;
      case 'shader':
        return <DigitalCardShaderLayer {...props} />;
      case 'text':
        return <DigitalCardTextLayer {...props} />;
      default:
        return null;
    }
  },
} as const;

export default DigitalCardLayer;
